import React, {useEffect, useRef, useState} from "react";
import {Radio, Row, Spin, Table, Typography} from "antd";
import dayjs from "dayjs";

import EngageCurrency from "../../../components/billings/EngageCurrency";
import {dateFormat} from "../../../lib/time";
import {userHasRole} from "../../../lib/role";
import PromotionalCreditAddDrawer from "./PromotionalCreditAddDrawer";
import {getAllPromotionCredits} from "../../../helpers/promotion";
import {messageType, showMessages} from "../../../helpers/message";
import BalanceInfoCard from "../../../components/billings/BalanceInfoCard";

import "./PromotionalCredits.less";

const PromotionalCredits = ({isOwner}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const {innerWidth} = window;
    const isMarketingOrMCT = userHasRole('staff', 'marketing') ||
        userHasRole('staff', 'developer') ||
        userHasRole('staff', 'member-care');

    // Pagination states.
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    // Promotion Credits table states.
    const [promotionalCredits, setPromotionalCredits] = useState([]);
    const [activeTabFilter, setActiveTabFilter] = useState('all');
    const [tableRefresh, setTableRefresh] = useState(false);
    const [totalBalance, setTotalBalance] = useState(0);

    const handleTabChange = (e) => {
        setLoading(true);
        setActiveTabFilter(e.target.value);
        setPage(1);
        setLoading(false);
    }

    const renderColumns = () => {
        let columns = [
            {
                title: <Typography.Text strong>Name</Typography.Text>,
                dataIndex: 'name',
                key: 'name',
                render: name => {
                    return <span className="long-text-wrap">{name}</span>
                },
            },
            {
                title: <Typography.Text strong>Effective Date</Typography.Text>,
                dataIndex: 'started_at',
                key: 'started_at',
                render: started_at => {
                    return <span
                        className="long-text-wrap">{dayjs(started_at).tz().format(dateFormat)}</span>
                },
            },
            {
                title: <Typography.Text strong>Expiry Date</Typography.Text>,
                dataIndex: 'expired_at',
                key: 'expired_at',
                render: expired_at => {
                    return <span
                        className="long-text-wrap">{expired_at ? dayjs(expired_at).tz().format(dateFormat) : 'N/A'}</span>
                },
            },
            {
                title: <Typography.Text strong>Original Balance</Typography.Text>,
                dataIndex: 'original_amount',
                key: 'original_amount',
                render: original_amount => {
                    return <EngageCurrency amount={parseFloat(original_amount)}/>
                },
            },
            {
                title: <Typography.Text strong>Remaining Balance</Typography.Text>,
                key: 'remaining_balance',
                render: (promoCredit) => {
                    const today = dayjs().tz();
                    let remainingBalance = parseFloat(promoCredit.current_amount);
                    // If today is after the expired_at date, the remaining balance is set to zero.
                    if (today.isAfter(dayjs(promoCredit.expired_at).tz(), 'day')) {
                        remainingBalance = 0.00;
                    }
                    return <EngageCurrency amount={remainingBalance}/>
                },
            },
            {
                title: <Typography.Text strong>Expired Balance</Typography.Text>,
                key: 'expired_balance',
                render: (promoCredit) => {
                    const today = dayjs().tz();

                    // Expired amount will default to zero until finalized.
                    let expiredBalance = promoCredit.expired_amount;
                    // If it's not finalized yet, but is expired, use the current_amount as the expired amount.
                    if (!promoCredit.finalized_at && today.isAfter(dayjs(promoCredit.expired_at).tz(), 'day')) {
                        expiredBalance = promoCredit.current_amount;
                    }
                    return <EngageCurrency amount={parseFloat(expiredBalance)}/>
                },
            },
        ];

        if (isOwner && !isMarketingOrMCT) {
            // If not marketing/MCT but isOwner, remove original balance and expired balance columns.
            columns = [columns[0], columns[1], columns[2], columns[4]];
        }

        // When in mobile, only show name column.
        return innerWidth <= 650 ? columns.slice(0, 1) : columns;
    }

    // Expandable rows for mobile view.
    const expandedRowRender = (record) => {
        let remainingBalance = parseFloat(record.current_amount);
        // If today is after the expired_at date, the remaining balance is set to zero.
        const today = dayjs().tz();
        if (today.isAfter(dayjs(record.expired_at).tz(), 'day')) {
            remainingBalance = 0.00;
        }

        let expandRows = [
            <li key={0} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Effective Date: </Typography.Text>
                {dayjs(record.started_at).tz().format(dateFormat)}
            </li>,
            <li key={1} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Expiry Date: </Typography.Text>
                {dayjs(record.expired_at).tz().format(dateFormat)}
            </li>,
            <li key={2} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Original Balance: </Typography.Text>
                <EngageCurrency amount={parseFloat(record.original_amount)}/>
            </li>,
            <li key={3} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Remaining Balance: </Typography.Text>
                <EngageCurrency amount={remainingBalance}/>
            </li>,
            <li key={4} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Expired Balance: </Typography.Text>
                <EngageCurrency amount={parseFloat(record.original_amount) - parseFloat(record.current_amount)}/>
            </li>,
        ];

        if (isOwner && !isMarketingOrMCT) {
            // If not marketing/MCT but isOwner, remove original balance and expired balance rows.
            expandRows = [expandRows[0], expandRows[1], expandRows[3]];
        }

        return (
            <ul className="expanded-row-list">
                {expandRows}
            </ul>
        )
    }

    // Handle mount and update.
    useEffect(() => {
        const fetchPromotionalCredits = async () => {
            setLoading(true);
            try {
                const abortSignal = abortSignalRef.current;
                const response = await getAllPromotionCredits(page, perPage, activeTabFilter, abortSignal);

                if (response && abortSignal.aborted === false) {
                    setPromotionalCredits(response.data);
                    setTotalRows(response.meta.total);

                    if (activeTabFilter === 'all') {
                        // When a new promo credit is invoked, the activeTabFilter is set to 'all' to update the total.
                        setTotalBalance(Number(response.total_balance));
                    }
                }
                setLoading(false);
            } catch (e) {
                showMessages(e.message, messageType.error);
                setLoading(false);
                if(e.status_code === 404) window.location.replace('/404');
            }
            setTableRefresh(false);
        }

        if (isMarketingOrMCT || isOwner) {
            fetchPromotionalCredits();
        }
    }, [page, perPage, activeTabFilter, tableRefresh, isMarketingOrMCT, isOwner]);

    //Pagination logic.
    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
    }

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        onChange,
        showSizeChanger: true,
        simple: innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    }

    return (
        <>
            {isOwner ?
                <Row className="universal-bottom-spacing">
                    <BalanceInfoCard balanceType={'promotional credits'} balance={totalBalance}/>
                </Row>
                : null
            }
            <Spin spinning={loading}>
                {!isOwner
                    ?
                    <Typography.Text className="role-disclaimer-text">
                        Sorry, you do not have the required permissions to view this page.
                    </Typography.Text>
                    :
                    <Table
                        rowKey="id"
                        dataSource={promotionalCredits}
                        columns={renderColumns()}
                        expandable={innerWidth <= 650 ? {expandedRowRender} : null}
                        bordered={false}
                        pagination={paginationAttr}
                        style={{marginLeft: '1px'}}
                        title={() =>
                            <div className="promo-credits-title-tab-bar">
                                <div className="promo-credits-mobile-title-button">
                                    <div>
                                        <h4 className="promo-credits-table-title">Promotional Credits</h4>
                                    </div>
                                    {innerWidth <= 650 && isMarketingOrMCT ?
                                        // When mobile, add button must be on top of radio buttons.
                                        // Remove completely if not marketing/MCT.
                                        <>
                                            <PromotionalCreditAddDrawer
                                                addButtonText="Add Promo Credit"
                                                setTableRefresh={setTableRefresh}
                                                setActiveTabFilter={setActiveTabFilter}
                                            />
                                        </>
                                        : null
                                    }
                                </div>
                                <div className="promo-credits-table-btn-container">
                                    <Radio.Group
                                        className="promo-credits-table-filter-btn-container"
                                        value={activeTabFilter}
                                        onChange={handleTabChange}
                                    >
                                        <Radio.Button value={'all'}>All</Radio.Button>
                                        <Radio.Button value={'current'}>{innerWidth < 340 ? 'Crt' : 'Current'}</Radio.Button>
                                        <Radio.Button value={'expired'}>{innerWidth < 340 ? 'Exp' : 'Expired'}</Radio.Button>
                                    </Radio.Group>
                                    {innerWidth > 650 && isMarketingOrMCT ?
                                        <>
                                            <PromotionalCreditAddDrawer
                                                addButtonText={innerWidth <= 800 ? "Add Credit" : "Add Promotional Credit"}
                                                setTableRefresh={setTableRefresh}
                                                setActiveTabFilter={setActiveTabFilter}
                                            />
                                        </>
                                        : null
                                    }
                                </div>
                            </div>}
                    />}
            </Spin>
        </>
    )
}

export default PromotionalCredits;

PromotionalCredits.propTypes = {};
